<template>
    <div
        @click="openPicker"
        v-if="!loading"
        class="px-3 sm:px-2 py-2 sm:py-1.5 w-14 sm:w-48 md:w-52 flex items-center justify-center gap-1.5 sm:gap-2 bg-blue-500 text-white rounded mx-4 sm:mx-5 active:bg-blue-600 hover:sm:bg-blue-600 transition-colors touch-manipulation"
    >
        <PlusCircleSvg class="size-5 sm:size-6 stroke-2 flex-shrink-0" />
        <div class="font-bold text-sm hidden sm:block whitespace-nowrap">Выбрать файл</div>
    </div>
    <div
        v-else
        class="px-3 sm:px-2 py-2 sm:py-1.5 w-full sm:w-48 md:w-52 flex items-center justify-center gap-1.5 sm:gap-2 bg-blue-500 text-white rounded mx-4 sm:mx-5"
    >
        <LoadingSvg class="size-5 sm:size-6 animate-spin flex-shrink-0" />
    </div>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue'
import { Api } from "@/service/api";
import {GOOGLE_PROJECT_ID, SITE_URL} from "@/service/config";
import PlusCircleSvg from "@/components/UI/svg/PlusCircleSvg.vue";
import LoadingSvg from "@/components/UI/LoadingSvg.vue";

const props = defineProps({
    buttonText: {
        type: String,
        default: 'Выбрать файл из Google Диска'
    },
    mimeTypes: {
        type: Array,
        default: () => ['application/vnd.google-apps.spreadsheet']
    },
    multiple: {
        type: Boolean,
        default: false
    },
    includeFolders: {
        type: Boolean,
        default: false
    },
    apiKey: {
        type: String,
        default: ''
    },
    scope: {
        type: String,
        default: 'https://www.googleapis.com/auth/drive.readonly'
    }
})

const emit = defineEmits([
    'picker-loaded',
    'picker-opened',
    'picker-closed',
    'file-selected',
])

const loading = ref(false)
const error = ref(null)
const picker = ref(null)
const isApiLoaded = ref(false)
const accessToken = ref(null)

// Простая загрузка Google API
const loadGoogleApi = () => {
    return new Promise((resolve, reject) => {
        if (isApiLoaded.value) {
            resolve()
            return
        }

        // Проверяем, если API уже загружено
        if (window.gapi && window.google && window.google.picker) {
            isApiLoaded.value = true
            resolve()
            return
        }

        const scriptId = 'google-picker-script'

        // Если скрипт уже загружается
        if (document.getElementById(scriptId)) {
            const checkLoaded = setInterval(() => {
                if (window.gapi && window.google && window.google.picker) {
                    clearInterval(checkLoaded)
                    isApiLoaded.value = true
                    resolve()
                }
            }, 100)
            setTimeout(() => clearInterval(checkLoaded), 5000)
            return
        }

        const script = document.createElement('script')
        script.id = scriptId
        script.src = 'https://apis.google.com/js/api.js'
        script.async = true
        script.defer = true

        let loaded = false

        script.onload = () => {
            if (loaded) return
            loaded = true
            // Используем простой load без timeout параметров
            window.gapi.load('picker', () => {
                isApiLoaded.value = true
                emit('picker-loaded')
                resolve()
            })
        }

        script.onerror = (err) => {
            if (loaded) return
            loaded = true
            console.error('Error loading Google API script:', err)
            error.value = 'Ошибка загрузки Google API'
            reject(error.value)
        }

        // Добавляем таймаут на случай если скрипт не загрузится
        setTimeout(() => {
            if (!loaded) {
                loaded = true
                console.error('Google API script load timeout')
                error.value = 'Таймаут загрузки Google API'
                emit('error', error.value)
                reject(error.value)
            }
        }, 10000)

        document.head.appendChild(script)
    })
}

// Получение access token
const getAccessToken = async () => {
    try {
        const token = await Api.getUserGoogleAccessToken()

        if (!token || !token.access_token) {
            throw new Error('Не удалось получить access token')
        }

        accessToken.value = token
        return token
    } catch (err) {
        console.error('Error getting access token:', err)
        throw new Error('Ошибка получения токена доступа: ' + err.message)
    }
}

// Обработчик выбора файлов
const pickerCallback = (data) => {
    // Сбрасываем пикер после использования
    picker.value = null
    emit('picker-closed')

    if (data.action === window.google.picker.Action.PICKED) {
        const files = Array.isArray(data.docs) ? data.docs : [data.docs]
        emit('file-selected', files)
    } else if (data.action === window.google.picker.Action.CANCEL) {
        console.log('Picker cancelled')
    }
}

// Создание пикера
const createPicker = () => {
    try {
        // Создаем вид для Google Документов (включая Таблицы, Документы, Презентации)
        const docsView = new window.google.picker.DocsView()
            .setIncludeFolders(props.includeFolders);

        if (props.mimeTypes.length > 0) {
            docsView.setMimeTypes(props.mimeTypes.join(','));
        }

        const pickerBuilder = new window.google.picker.PickerBuilder()
            .addView(docsView) // Добавляем наш настроенный вид
            .setOAuthToken(accessToken.value.access_token)
            .setAppId(GOOGLE_PROJECT_ID) // Убедитесь, что это ID проекта или Client ID без суффикса
            .setCallback(pickerCallback)
            .setOrigin(SITE_URL)

        if (props.multiple) {
            pickerBuilder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
        }

        // Если вам нужен только DocsView, нет необходимости добавлять второй view.
        // Если вам нужны другие виды (например, DriveView для всех файлов), добавьте их сюда:
        // pickerBuilder.addView(new window.google.picker.DriveView());

        picker.value = pickerBuilder.build();

    } catch (err) {
        console.error('Error creating picker:', err);
        // Возможно, лучше не выбрасывать ошибку, а отобразить её пользователю или записать в лог.
        throw new Error('Ошибка создания пикера: ' + err.message);
    }
};


// Открытие пикера
const openPicker = async () => {
    try {
        loading.value = true
        error.value = null

        // Получаем access token
        await getAccessToken()

        // Загружаем API
        await loadGoogleApi()

        // Проверяем, что все необходимое загружено
        if (!window.google || !window.google.picker) {
            throw new Error('Google Picker API не загружена')
        }

        // Всегда создаем новый пикер
        createPicker()

        // Открываем пикер
        if (picker.value) {
            picker.value.setVisible(true)
            emit('picker-opened')
        } else {
            throw new Error('Пикер не был создан')
        }

    } catch (err) {
        console.error('Error in openPicker:', err)
        error.value = err.message
        emit('error', err.message)
    } finally {
        loading.value = false
    }
}

// Альтернативный метод загрузки - более простой
const loadGoogleApiSimple = () => {
    return new Promise((resolve) => {
        if (window.google && window.google.picker) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = 'https://apis.google.com/js/api.js'
        script.onload = () => {
            window.gapi.load('picker', { callback: resolve })
        }
        script.onerror = resolve // Продолжаем даже при ошибке
        document.head.appendChild(script)
    })
}

// Инициализация при монтировании
onMounted(async () => {
    try {
        // Предзагрузка API при монтировании с альтернативным методом
        await loadGoogleApiSimple()
    } catch (err) {
        console.warn('Warning in onMounted:', err)
        // Не блокируем инициализацию при ошибке предзагрузки
    }
})
</script>

<style scoped>
button {
    background: #4285f4;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
}

button:hover:not(:disabled) {
    background: #3367d6;
}

button:disabled {
    background: #ccc;
    cursor: not-allowed;
}

button.loading {
    opacity: 0.7;
}

.error-message {
    color: #d32f2f;
    margin-top: 8px;
    font-size: 14px;
    padding: 8px;
    background: #ffebee;
    border-radius: 4px;
    border: 1px solid #f44336;
}
</style>